import noUiSlider from 'nouislider';
import wNumb from 'wnumb';

/**
 * Range with limits
 */
export default class {
  /**
   * Component container
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #container;

  /**
   * @private
   *
   * @type {HTMLElement}
   */
  #range;

  /**
   * @private
   *
   * @type {HTMLElement}
   */
  #input;

  /**
   * @private
   *
   * @type {object}
   */
  #limits;

  /**
   * @private
   *
   * @type {object}
   */
  #limitLabels;

  /**
   * @private
   *
   * @type {number}
   */
  #step;

  /**
   * @private
   *
   * @type {?HTMLElement}
   */
  #tooltip;

  /**
   * @param {HTMLElement} container Component container
   */
  constructor(container) {
    this.#container = container;
    if (!this.#container) {
      throw new Error('CVRISK RangeLimits: container is not defined');
    }

    this.#range = this.#container.querySelector('.cvrisk-range');
    this.#input = this.#container.querySelector('.cvrisk-range-value');
    this.#limits = {
      min: Number(this.#range.dataset.min),
      max: Number(this.#range.dataset.max),
    };
    this.#limitLabels = {
      min: this.#range.dataset.minLabel,
      max: this.#range.dataset.maxLabel,
    };
    this.#step = Number(this.#range.dataset.step);
    this.#tooltip = null;

    this.#init();
  }

  /**
   * Get range start value from input value
   *
   * @private
   *
   * @return {number} Default 0
   */
  #start() {
    let val = this.#input.value;
    if (!val.length) {
      val = this.#limits.min;
    }

    return Number(val);
  }

  /**
   * Get the number of decimal places from the step value
   *
   * @private
   *
   * @return {number}
   */
  #decimals() {
    if (this.#step % 1 != 0) {
      return this.#step.toString().split('.')[1].length;
    }

    return 0;
  }

  /**
   * Get range formatter
   *
   * @private
   *
   * @return {object}
   *
   * @see https://refreshless.com/nouislider/number-formatting/
   */
  #formatter() {
    return wNumb({
      decimals: this.#decimals(),
    });
  }

  /**
   * Get pips options
   *
   * @private
   *
   * @return {object}
   */
  #pipsOption() {
    return {
      mode: 'range',
      density: -1,
      // https://refreshless.com/nouislider/number-formatting/
      format: {
        /**
         * @param {number} value
         *
         * @returns {number}
         */
        to: (value) => {
          // fix decimal for integer
          if (value % 1 !== 0) {
            value = Number(value.toFixed(this.#decimals()));
          }

          // format with label
          const label =
            value === this.#limits.min
              ? this.#limitLabels.min
              : this.#limitLabels.max;
          if (label) {
            // label MUST HAVE %d
            return label.replace('%d', value);
          }

          return value;
        },

        /**
         * @param {string} value
         *
         * @returns {number}
         */
        from: (value) => {
          return Number(value);
        },
      },
    };
  }

  /**
   * Initialize data, range and events
   *
   * @private
   *
   * @return {void}
   */
  #init() {
    noUiSlider.create(this.#range, {
      start: this.#start(),
      connect: [true, false],
      animate: true,
      range: this.#limits,
      tooltips: [true],
      step: this.#step,
      format: this.#formatter(),
      pips: this.#pipsOption(),
    });

    // Prevent navigation by TAB with the handle
    this.#range.querySelector('.noUi-handle').removeAttribute('tabindex');

    this.#setTooltip();
    this.#setInputValue();

    // Update inputs value after setting the range value
    this.#range.noUiSlider.on('set', () => this.#setInputValue());
    // Update tooltip value after changing the range value
    this.#range.noUiSlider.on('update', () => this.#setTooltipValue());
  }

  /**
   * Replacing the noUiSlider tooltip with an input and initializes events
   *
   * @private
   *
   * @return {void}
   */
  #setTooltip() {
    // retrieve noUISlider tooltip
    let tooltip = this.#range.noUiSlider.getTooltips()[0];

    if (tooltip) {
      // replace by custom tooltip
      let div = document.createElement('div');
      div.classList = 'cvrisk-range-tooltip';

      let input = document.createElement('input');
      input.type = 'text';
      input.value = this.#start();
      div.appendChild(input);

      tooltip.parentNode.appendChild(div);
      tooltip.style.display = 'none';

      // set tooltip
      this.#tooltip = div;

      // select all text on focus
      input.addEventListener('focus', (e) => {
        e.target.select();
      });
      // set range value on blur
      input.addEventListener('blur', (e) => this.setRangeValue(e.target.value));
      // trigger 'blur' by pressing the "Enter" key
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.target.blur();
        }
      });
    }
  }

  /**
   * Set tooltip input value from range value
   *
   * @private
   *
   * @return {void}
   */
  #setTooltipValue() {
    if (this.#tooltip) {
      this.#tooltip.querySelector('input').value = this.#range.noUiSlider.get();
    }
  }

  /**
   * Set input value from range value
   *
   * @private
   *
   * @return {void}
   */
  #setInputValue() {
    let value = Number(this.#range.noUiSlider.get());
    let current = Number(this.#input.value);

    // prevent unecessary event
    if (this.#input.value.length === 0 || value !== current) {
      // set form input
      this.#input.value = value;

      this.#container.dispatchEvent(
        new CustomEvent('updateValue', { detail: value })
      );
    }
  }

  /**
   * Set range value
   *
   * @param {number|string} value Range value
   *
   * @return {void}
   */
  setRangeValue(value) {
    let current = Number(this.#range.noUiSlider.get());
    value = Number(value);

    // prevent unecessary event
    if (value !== current) {
      if (value < this.#limits.min) {
        value = this.#limits.min;
      }

      if (value > this.#limits.max) {
        value = this.#limits.max;
      }

      this.#range.noUiSlider.set(value);
    }
  }

  /**
   * Update range options
   *
   * @param {object} options Range options
   *
   * @return {void}
   *
   * @see https://refreshless.com/nouislider/more/#section-update
   */
  updateOptions(options) {
    // update range
    if (options?.range) {
      this.#limits = options.range;
    }

    // update step
    if (options?.step) {
      this.#step = options.step;

      // update formatters
      options.pips = options.pips ?? this.#pipsOption();
      options.format = options.format ?? this.#formatter();
    }

    this.#range.noUiSlider.updateOptions(options);
  }

  /**
   * Get input value
   *
   * @return {number}
   */
  getValue() {
    return Number(this.#input.value);
  }

  /**
   * Attach an event on container element
   *
   * @param {string} event Event name
   * @param {function} cb Callback function
   *
   * @return {void}
   */
  on(event, cb) {
    this.#container.addEventListener(event, cb);
  }

  /**
   * Get HTML data from range element
   *
   * @param {string} key Data name
   *
   * @return {string}
   */
  getData(key) {
    return this.#range?.dataset[key] || null;
  }

  /**
   * Set range to minimum value
   *
   * @return {void}
   */
  reset() {
    this.setRangeValue(this.#limits?.min || 0);
  }
}
